/* global google */

import React, { useEffect, useRef, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import { useSelector, useDispatch } from "react-redux";
import polyline from "@mapbox/polyline";
import Icon from "assets/images/marker_blue.png";
import { fetchGeoFences } from "features/geoFence/geoFenceThunks";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import satellitedark from "assets/satellitedark.svg";
import satellitelight from "assets/satellitelight.svg";
import satellite from "assets/satellite.png";
import haltlight from "assets/haltlight.png";
import haltdark from "assets/haltdark.png";
import ignitionlight from "assets/ignitionlight.png";
import ignitiondark from "assets/ignitiondark.png";
import sosdark from "assets/sosdark.png";
import soslight from "assets/soslight.png";
import rfidlight from "assets/rfidlight.png";
import rfiddark from "assets/rfiddark.png";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import startFlag from "assets/red-flag.png";
import endFlag from "assets/racing-flag.png";
import { Card, Divider, MenuItem } from "@mui/material";
import { haltReport } from "config/apis";
import { setHaltData } from "features/routeReplay/routeReplaySlice";
import { ignition } from "config/apis";
import { setRFIDData, setIgnitionData } from "features/routeReplay/routeReplaySlice";

const btnStyle = {
  background: "#fff",
  padding: "4px",
  borderRadius: "8px",
  cursor: "pointer",
  border: "none",
  fontWeight: "600",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
};

// MODIFIED: thresholds prop is no longer used for logic
const RouteReplayMap = ({ thresholds }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const [mapType, setMapType] = useState("hybrid");
  const [haltMinutes, setHaltMinutes] = useState(4);
  const [showHaltMenu, setShowHaltMenu] = useState(false);

  const [showHalts, setShowHalts] = useState(false);
  const [showIgnition, setShowIgnition] = useState(false);
  const [showSOS, setShowSOS] = useState(false);
  const [showRFID, setShowRFID] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const account = useSelector((state) => state.account.data?.accountModel?.account);
  const highlightSegment = useSelector((state) => state.routeReplay.highlightSegment);
  const fullName = account ? `${account.firstName || ""} ${account.lastName || ""}`.trim() : "";
  const orgName = localStorage.getItem("orgName");
  const vehicleId = useSelector((state) => state.vehicle.isVehicle);
  const routeDevices = useSelector((state) => state.routeReplay.devices);
  const startTimeStored = useSelector((state) => state.vehicle.selectedStartTime);
  const endTimeStored = useSelector((state) => state.vehicle.selectedEndTime);

  const dispatch = useDispatch();
  const routeData = useSelector((state) => state.routeReplay);
  const currentPacket = useSelector((state) => state.routeReplay.currentPacket);
  const journeyPlot = useSelector((state) => state.routeReplay.journeyPlot);
  const haltData = useSelector((state) => state.routeReplay.haltData);
  const ignitionData = useSelector((state) => state.routeReplay.ignitionData);
  const sosMarkers = useSelector((state) => state.routeReplay.sosMarkers);
  const rFIDData = useSelector((state) => state.routeReplay.rFIDData);

  const allLocationRanges = useSelector((state) => state.geoFence.allLocationRanges);

  const devicePackets = useMemo(() => {
    if (!routeData.devices || !routeData.devices.devices) return [];
    return Object.entries(routeData.devices.devices)
      .filter(([key]) => !isNaN(key))
      .map(([timestamp, data]) => ({
        timestamp: Number(timestamp),
        ...data,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [routeData]);

  const handleHaltChange = async (mins) => {
    setHaltMinutes(mins);
    // setShowHaltMenu(false);

    if (!vehicleId || !startTimeStored || !endTimeStored) {
      console.error("Missing required data for Halt API");
      return;
    }

    const token = localStorage.getItem("token");
    const formatForApi = (dateTime) => dateTime.replace(/-/g, "/").replace("T", " ");

    const body = {
      zone: "Asia/Kolkata",
      startTime: formatForApi(startTimeStored),
      endTime: formatForApi(endTimeStored),
      things: vehicleId,
      interval: mins,
      geoLat: 0,
      geoLong: 0,
      geoFenceName: "",
      thingIds: [],
      geofence: [],
      groups: [],
      orgName: ""
    };

    try {
      const response = await fetch(haltReport, {
        method: "POST",
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        // 1. Redux update karein
        dispatch(setHaltData(data));
        // 2. State trigger karein taaki useEffect refresh ho
        setShowHalts(true);
        setTimeout(() => setShowHalts(true), 10);
      }
    } catch (err) {
      console.error("Halt API Error:", err);
    }
  };

  // RESTORED: Hardcoded speed logic
  const getColor = (speed) => {
    if (speed < thresholds.low) return "rgb(43, 191, 159)";      // Slow
    if (speed < thresholds.med) return "rgb(251, 164, 0)";      // Medium
    if (speed < thresholds.high) return "rgb(240, 120, 70)";    // High
    return "#ff0000";                                           // Extreme
  };

  useEffect(() => {
    if (window.google && mapRef.current && !mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 3.2028, lng: 73.2207 },
        zoom: 12,
        // zoom: 13,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeId: mapType,
      });
      mapInstance.current.globalInfoWindow = new window.google.maps.InfoWindow();

      window.google.maps.event.addListenerOnce(mapInstance.current, 'idle', () => {
        window.google.maps.event.trigger(mapInstance.current, 'resize');
      });
    }
  }, []);

  useEffect(() => { dispatch(fetchGeoFences()); }, [dispatch]);

  useEffect(() => {
    if (!mapInstance.current) return;

    // Cleanup
    if (mapInstance.current.highlighter) {
      mapInstance.current.highlighter.setMap(null);
      mapInstance.current.highlighter = null;
    }

    if (!highlightSegment || !devicePackets.length) return;

    const { startTime, endTime, type } = highlightSegment;

    // Date parsing with fallback
    const parseToMs = (dateStr) => {
      if (!dateStr) return 0;
      // .0 milliseconds ko remove karke standard format banate hain
      const cleanStr = dateStr.toString().split('.')[0].replace(/\//g, "-").trim();
      return new Date(cleanStr).getTime();
    };

    const startTs = parseToMs(startTime);
    const endTs = parseToMs(endTime);

    // Filter Packets with strict Number conversion
    const tripPoints = devicePackets
      .filter(p => {
        const pTs = Number(p.timestamp);
        // 1000ms (1 second) ka buffer rakhte hain comparison me safe rehne ke liye
        return pTs >= (startTs - 1000) && pTs <= (endTs + 1000);
      })
      .map(p => ({ lat: p.location.latitude, lng: p.location.longitude }));

    if (tripPoints.length >= 2) {
      const highlightPoly = new window.google.maps.Polyline({
        path: tripPoints,
        strokeColor: type === "IGNITION" ? "#FF0000" : "#2196F3",
        strokeOpacity: 1.0,
        strokeWeight: 8,
        zIndex: 1000,
      });

      highlightPoly.setMap(mapInstance.current);
      mapInstance.current.highlighter = highlightPoly;

      const bounds = new window.google.maps.LatLngBounds();
      tripPoints.forEach(pt => bounds.extend(pt));
      mapInstance.current.fitBounds(bounds);
    } else {
      // Agar fir bhi points nahi mile, toh sirf map ko wahan le jao (Flicker fix)
      if (highlightSegment.startLat && highlightSegment.startLng) {
        mapInstance.current.panTo({ lat: highlightSegment.startLat, lng: highlightSegment.startLng });
        mapInstance.current.setZoom(16);
      }
    }
  }, [highlightSegment, devicePackets]);

  // RouteReplayMap.js ke andar is useEffect ko replace karein:

  useEffect(() => {
    if (!mapInstance.current) return;

    // 1. Purani saari Polylines ko map se hatayein
    if (mapInstance.current.polylines) {
      mapInstance.current.polylines.forEach((p) => p.setMap(null));
      mapInstance.current.polylines = [];
    }

    // 2. Purane Start aur End markers (flags) ko clear karein
    if (mapInstance.current.startEndMarkers) {
      mapInstance.current.startEndMarkers.forEach((m) => m.setMap(null));
      mapInstance.current.startEndMarkers = [];
    }

    // Agar data nahi hai toh aage na badhein
    if (!devicePackets || devicePackets.length < 2) {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      return;
    }

    // 3. Nayi Colored Polyline segments draw karein
    mapInstance.current.polylines = [];

    for (let i = 0; i < devicePackets.length - 1; i++) {
      const start = devicePackets[i];
      const end = devicePackets[i + 1];

      // Speed calculation (KM/H conversion)
      // const currentSpeed = (start.speed || 0) * 3.6;
      const currentSpeed = (start.speed || 0) * 1.852;
      const segmentPolyline = new window.google.maps.Polyline({
        path: [
          { lat: start?.location?.latitude, lng: start?.location?.longitude },
          { lat: end?.location?.latitude, lng: end?.location?.longitude }
        ],
        geodesic: true,
        // Yahan aapka dynamic getColor function use ho raha hai jo thresholds use karta hai
        strokeColor: getColor(currentSpeed),
        strokeOpacity: 1.0,
        strokeWeight: 4,
        zIndex: 10
      });

      segmentPolyline.setMap(mapInstance.current);
      mapInstance.current.polylines.push(segmentPolyline);
    }

    // 4. Start aur End Flags (Markers) ko wapas lagayein
    mapInstance.current.startEndMarkers = [];
    const firstPacket = devicePackets[0];
    const lastPacket = devicePackets[devicePackets.length - 1];

    // Start Flag
    const startMarker = new window.google.maps.Marker({
      position: { lat: firstPacket.location.latitude, lng: firstPacket.location.longitude },
      map: mapInstance.current,
      title: "Start Point",
      icon: {
        url: startFlag,
        scaledSize: new window.google.maps.Size(35, 35),
        anchor: new window.google.maps.Point(5, 35)
      },
      zIndex: 50
    });

    // End Flag
    const endMarker = new window.google.maps.Marker({
      position: { lat: lastPacket.location.latitude, lng: lastPacket.location.longitude },
      map: mapInstance.current,
      title: "End Point",
      icon: {
        url: endFlag,
        scaledSize: new window.google.maps.Size(35, 35),
        anchor: new window.google.maps.Point(5, 35)
      },
      zIndex: 50
    });

    mapInstance.current.startEndMarkers.push(startMarker, endMarker);

    // 5. Moving Car marker ko initialize ya reposition karein
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map: mapInstance.current,
        icon: {
          url: Icon,
          scaledSize: new window.google.maps.Size(45, 45),
          anchor: new window.google.maps.Point(20, 40)
        },
        zIndex: 100
      });
    }

    markerRef.current.setPosition({
      lat: devicePackets[0]?.location?.latitude,
      lng: devicePackets[0]?.location?.longitude
    });

    // CRITICAL: Dependency array mein 'thresholds' add kiya gaya hai
  }, [devicePackets, thresholds]);

  useEffect(() => {
    if (mapInstance.current && devicePackets.length > 0) {
      const firstPacket = devicePackets[0];
      const firstPos = {
        lat: Number(firstPacket.location.latitude),
        lng: Number(firstPacket.location.longitude)
      };

      // Map ko gadi ki location par move karo
      mapInstance.current.panTo(firstPos);

      // Zoom badha kar 15 kar do
      mapInstance.current.setZoom(15);

      // Force refresh for gray screen fix
      window.google.maps.event.trigger(mapInstance.current, 'resize');
    }
  }, [devicePackets]);

  // RESTORED: Dependency array no longer includes thresholds
  // useEffect(() => {
  //   if (!mapInstance.current) return;

  //   // 1. Purani Polylines clear karein
  //   if (mapInstance.current.polylines) {
  //     mapInstance.current.polylines.forEach((p) => p.setMap(null));
  //     mapInstance.current.polylines = [];
  //   }

  //   // 2. Purane Start/End Flags clear karein
  //   if (mapInstance.current.startEndMarkers) {
  //     mapInstance.current.startEndMarkers.forEach((m) => m.setMap(null));
  //     mapInstance.current.startEndMarkers = [];
  //   }

  //   if (devicePackets.length < 2) {
  //     if (markerRef.current) {
  //       markerRef.current.setMap(null);
  //       markerRef.current = null;
  //     }
  //     return;
  //   }

  //   // 3. Polyline Draw karein
  //   mapInstance.current.polylines = [];
  //   for (let i = 0; i < devicePackets.length - 1; i++) {
  //     const start = devicePackets[i];
  //     const end = devicePackets[i + 1];
  //     const segmentPolyline = new window.google.maps.Polyline({
  //       path: [{ lat: start?.location?.latitude, lng: start?.location?.longitude }, { lat: end?.location?.latitude, lng: end?.location?.longitude }],
  //       geodesic: true,
  //       strokeColor: getColor(start.speed * 3.6),
  //       strokeOpacity: 1.0,
  //       strokeWeight: 4,
  //       zIndex: 10
  //     });
  //     segmentPolyline.setMap(mapInstance.current);
  //     mapInstance.current.polylines.push(segmentPolyline);
  //   }

  //   // --- START AND END FLAGS ADDED HERE ---
  //   mapInstance.current.startEndMarkers = [];

  //   const firstPacket = devicePackets[0];
  //   const lastPacket = devicePackets[devicePackets.length - 1];

  //   // Start Flag (Red)
  //   const startMarker = new window.google.maps.Marker({
  //     position: { lat: firstPacket.location.latitude, lng: firstPacket.location.longitude },
  //     map: mapInstance.current,
  //     title: "Start Point",
  //     icon: {
  //       url: startFlag,
  //       scaledSize: new window.google.maps.Size(35, 35),
  //       anchor: new window.google.maps.Point(5, 35) // Flag pole ke base pe anchor
  //     },
  //     zIndex: 50
  //   });

  //   // End Flag (Racing/Checkered)
  //   const endMarker = new window.google.maps.Marker({
  //     position: { lat: lastPacket.location.latitude, lng: lastPacket.location.longitude },
  //     map: mapInstance.current,
  //     title: "End Point",
  //     icon: {
  //       url: endFlag,
  //       scaledSize: new window.google.maps.Size(35, 35),
  //       anchor: new window.google.maps.Point(5, 35)
  //     },
  //     zIndex: 50
  //   });

  //   mapInstance.current.startEndMarkers.push(startMarker, endMarker);
  //   // --------------------------------------

  //   if (!markerRef.current) {
  //     markerRef.current = new window.google.maps.Marker({
  //       map: mapInstance.current,
  //       icon: { url: Icon, scaledSize: new window.google.maps.Size(45, 45), anchor: new window.google.maps.Point(20, 40) },
  //       zIndex: 100 // Moving car hamesha flags ke upar rahegi
  //     });
  //   }
  //   markerRef.current.setPosition({ lat: devicePackets[0]?.location?.latitude, lng: devicePackets[0]?.location?.longitude });
  // }, [devicePackets]);

  useEffect(() => {
    if (currentPacket && markerRef.current && mapInstance.current) {
      const lat = currentPacket?.location?.latitude;
      const lng = currentPacket?.location?.longitude;
      if (typeof lat === "number" && typeof lng === "number" && isFinite(lat) && isFinite(lng)) {
        const newPos = { lat, lng };
        markerRef.current.setPosition(newPos);
        mapInstance.current.panTo(newPos);
      }
    }
  }, [currentPacket]);

  useEffect(() => { if (mapInstance.current) mapInstance.current.setMapTypeId(mapType); }, [mapType]);

  useEffect(() => {
    if (!mapInstance.current || !allLocationRanges.length) return;
    if (mapInstance.current.circles) mapInstance.current.circles.forEach((c) => c.setMap(null));
    mapInstance.current.circles = [];
    allLocationRanges.forEach((geo) => {
      const { latitude, longitude } = geo?.data?.location || {};
      if (latitude == null || longitude == null) return;
      const circle = new window.google.maps.Circle({
        strokeColor: "#000000", strokeOpacity: 0.8, strokeWeight: 2, fillColor: "#000000", fillOpacity: 0.35,
        map: mapInstance.current, center: { lat: latitude, lng: longitude }, radius: geo.data.radius,
      });
      mapInstance.current.circles.push(circle);
    });
  }, [allLocationRanges]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.stopMarkers) mapInstance.current.stopMarkers.forEach((m) => m.setMap(null));
    mapInstance.current.stopMarkers = [];
    if (!journeyPlot.stopPoints || !journeyPlot.stopPoints.length) return;
    journeyPlot.stopPoints.forEach((stop, idx) => {
      const marker = new window.google.maps.Marker({
        position: { lat: stop.location.latitude, lng: stop.location.longitude },
        map: mapInstance.current,
        label: { text: `${idx + 1}`, color: "#fff", fontWeight: "bold" },
        icon: { url: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><circle cx='16' cy='16' r='14' fill='black' stroke='white' stroke-width='2'/></svg>", scaledSize: new window.google.maps.Size(32, 32) },
      });
      marker.addListener("click", () => {
        const info = mapInstance.current.globalInfoWindow;
        if (info.getAnchor() === marker) { info.close(); }
        else {
          info.setContent(`<div style="font-weight:bold; color:#000; padding:5px;">${stop.locationName || "Stop " + (idx + 1)}</div>`);
          info.open(mapInstance.current, marker);
        }
      });
      mapInstance.current.stopMarkers.push(marker);
    });
  }, [journeyPlot.stopPoints]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.journeyPolyline) mapInstance.current.journeyPolyline.setMap(null);
    if (!journeyPlot.encodedPath) return;
    const coords = polyline.decode(journeyPlot.encodedPath).map(([lat, lng]) => ({ lat, lng }));
    const journeyPolyline = new window.google.maps.Polyline({
      path: coords, geodesic: true, strokeColor: "#4f4f4e", strokeOpacity: 1.0, strokeWeight: 4, zIndex: 9999,
    });
    journeyPolyline.setMap(mapInstance.current);
    mapInstance.current.journeyPolyline = journeyPolyline;
  }, [journeyPlot.encodedPath]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.haltMarkers) mapInstance.current.haltMarkers.forEach((m) => m.setMap(null));
    mapInstance.current.haltMarkers = [];
    if (!showHalts || !haltData || !haltData.length) return;
    haltData.forEach((halt) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(halt.haltLatitude), lng: Number(halt.haltLongitude) },
        map: mapInstance.current,
        icon: { url: haltlight, scaledSize: new window.google.maps.Size(30, 30) },
      });
      marker.addListener("click", () => {
        const info = mapInstance.current.globalInfoWindow;
        if (info.getAnchor() === marker) { info.close(); }
        else {
          const content = `<div style="padding: 10px; color: #000; font-family: sans-serif;">
              <div style="font-weight: bold; border-bottom: 1px solid #eee; margin-bottom: 5px;">Halt Details</div>
              <div style="font-size: 13px;"><strong>Duration:</strong> ${halt.timeInMinutes} Minutes</div>
              <div style="font-size: 12px; color: #666;">Time: ${halt.timeCalculate}</div>
            </div>`;
          info.setContent(content);
          info.open(mapInstance.current, marker);
        }
      });
      mapInstance.current.haltMarkers.push(marker);
    });
  }, [haltData, showHalts]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.ignMarkers) mapInstance.current.ignMarkers.forEach((m) => m.setMap(null));
    mapInstance.current.ignMarkers = [];
    if (!showIgnition || !ignitionData || !ignitionData.length) return;
    ignitionData.forEach((ign) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(ign.ignStartLatitude), lng: Number(ign.ignStartLongitude) },
        map: mapInstance.current,
        icon: { url: ignitionlight, scaledSize: new window.google.maps.Size(30, 30) },
      });
      marker.addListener("click", () => {
        const info = mapInstance.current.globalInfoWindow;
        if (info.getAnchor() === marker) { info.close(); }
        else {
          const content = `<div style="padding: 10px; color: #000; font-family: sans-serif;"><div style="font-weight: bold; border-bottom: 1px solid #eee; margin-bottom: 5px;">Ignition Details</div><div style="font-size: 13px;"><strong>Status:</strong> ${ign.ignitionStatus}</div><div style="font-size: 12px; color: #666;">Start Time: ${ign.ignStartTimeDate}</div></div>`;
          info.setContent(content);
          info.open(mapInstance.current, marker);
        }
      });
      mapInstance.current.ignMarkers.push(marker);
    });
  }, [ignitionData, showIgnition]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.sosMarkers) mapInstance.current.sosMarkers.forEach((m) => m.setMap(null));
    mapInstance.current.sosMarkers = [];
    if (!showSOS || !sosMarkers || !sosMarkers.length) return;
    sosMarkers.forEach((sos) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(sos.latitude), lng: Number(sos.longitude) },
        map: mapInstance.current,
        icon: { url: soslight, scaledSize: new window.google.maps.Size(35, 35) },
      });
      mapInstance.current.sosMarkers.push(marker);
    });
  }, [sosMarkers, showSOS]);

  useEffect(() => {
    if (!mapInstance.current) return;
    if (mapInstance.current.rfidMarkers) mapInstance.current.rfidMarkers.forEach((m) => m.setMap(null));
    mapInstance.current.rfidMarkers = [];
    if (!showRFID || !rFIDData?.rowList || rFIDData.rowList.length === 0) return;
    rFIDData.rowList.forEach((rfid) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(rfid.latitude), lng: Number(rfid.longitude) },
        map: mapInstance.current,
        icon: { url: rfidlight, scaledSize: new window.google.maps.Size(35, 35) },
      });
      marker.addListener("click", () => {
        const info = mapInstance.current.globalInfoWindow;
        if (info.getAnchor() === marker) { info.close(); }
        else {
          const content = `<div style="padding: 10px; color: #000;"><div style="font-weight: bold; margin-bottom: 5px;">RFID Swipe</div><div style="font-size: 13px;"><strong>Name:</strong> ${rfid.accountName || "Unknown"}</div><div style="font-size: 13px;"><strong>ID:</strong> ${rfid.accessId}</div></div>`;
          info.setContent(content);
          info.open(mapInstance.current, marker);
        }
      });
      mapInstance.current.rfidMarkers.push(marker);
    });
  }, [rFIDData, showRFID]);

  const handleLogout = async () => {
    const token = localStorage.getItem("token");

    try {
      if (token) {
        await axios.get("https://v3.dhundhoo.com/auth/signout", {
          headers: {
            "x-dhundhoo-session": token,
          },
        });
      }
    } catch (error) {
      console.error("Logout API failed, clearing token anyway");
    } finally {
      // API fail ho ya success — token clear hoga hi
      localStorage.removeItem("token");
      navigate("/authentication/sign-in");
    }
  };

  const handleHaltIconClick = () => {
    if (showHalts) {
      // Agar filter already active hai → remove karo
      setShowHalts(false);
      dispatch(setHaltData([]));

      if (mapInstance.current?.haltMarkers) {
        mapInstance.current.haltMarkers.forEach((m) => m.setMap(null));
        mapInstance.current.haltMarkers = [];
      }
    }

    setShowHaltMenu((prev) => !prev);
  };

  const handleIgnitionToggle = async () => {
    if (!showIgnition) {
      // If turning ON and no data exists, fetch it
      if (ignitionData.length === 0) {
        const token = localStorage.getItem("token");
        const todayDate = new Date().toISOString().split("T")[0];

        try {
          const response = await fetch(ignition, {
            method: "POST",
            headers: {
              "x-dhundhoo-session": token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              date: todayDate,
              zone: "Asia/Calcutta",
              startTime: startTimeStored.replace(/-/g, "/").replace("T", " "),
              endTime: endTimeStored.replace(/-/g, "/").replace("T", " "),
              things: vehicleId,
              thingIds: [], geofence: [], groups: [], interval: 0, orgName: "", geoFenceName: "", geoLat: 0, geoLong: 0
            }),
          });
          const data = await response.json();
          if (response.ok) dispatch(setIgnitionData(data));
        } catch (err) {
          console.error("Ignition Error:", err);
        }
      }
      setShowIgnition(true);
    } else {
      setShowIgnition(false);
    }
  };

  // 2. Logic for RFID Toggle
  const handleRFIDToggle = async () => {

    if (showRFID) {
      setShowRFID(false);
      return;
    }

    const hasData = rFIDData?.rowList && Array.isArray(rFIDData.rowList) && rFIDData.rowList.length > 0;

    if (!hasData) {

      const token = localStorage.getItem("token");

      if (!startTimeStored || !endTimeStored || !vehicleId) {
        console.error("Missing required parameters for RFID fetch");
        return;
      }

      try {
        const response = await fetch("https://v3.dhundhoo.com/reports/reports/access", {
          method: "POST",
          headers: {
            "x-dhundhoo-session": token,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            startTime: startTimeStored.replace(/-/g, "/").replace("T", " "),
            endTime: endTimeStored.replace(/-/g, "/").replace("T", " "),
            thingIds: [vehicleId],
            timeZone: "Asia/Kolkata"
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Redux mein data set karein
          dispatch(setRFIDData(data));
        }
      } catch (err) {
        console.error("RFID API Error:", err);
      }
    }

    setShowRFID(true);
  };

  return (
    <>
      <div style={{ position: "absolute", top: -30, right: 20, display: "flex", flexDirection: "column", gap: "10px", zIndex: 1, marginTop: '50px' }}>
        <button style={btnStyle} onClick={() => setMapType(mapType === "roadmap" ? "satellite" : "roadmap")} title="Toggle Map Type">
          <img src={mapType === "satellite" ? satellite : satellitedark} alt="Map" style={{ width: 22, height: 22 }} />
        </button>
        <div style={{ position: 'relative' }}>
          <button
            style={{
              ...btnStyle,
              backgroundColor: showHalts ? '#e3f2fd' : '#fff',
            }}
            // onClick={() => setShowHaltMenu(!showHaltMenu)}
            onClick={handleHaltIconClick}
            title="Halt Filter"
          >
            {/* Icon check: agar showHalts true hai toh light wala dikhao */}
            <img src={showHalts ? haltlight : haltdark} alt="Halt" style={{ width: 22, height: 22 }} />

          </button>

          {/* Halt Dropdown Menu */}
          {showHaltMenu && (
            <Card
              sx={{
                position: 'absolute',
                right: '50px',
                top: 0,
                zIndex: 1001,
                minWidth: '140px',
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                backgroundColor: '#fff'
              }}
            >
              <div style={{ padding: '8px', fontSize: '11px', fontWeight: 'bold', color: '#888', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                MIN. HALT TIME
              </div>
              {[3, 5, 7, 9, 15].map(m => (
                <MenuItem
                  key={m}
                  selected={m === haltMinutes}
                  onClick={() => {
                    handleHaltChange(m);
                    // setShowHaltMenu(false);
                  }}
                  sx={{ fontSize: '13px', py: 1 }}
                >
                  {m} Minutes
                </MenuItem>
              ))}
            </Card>
          )}
        </div>
        <button style={btnStyle} onClick={handleIgnitionToggle} title={showIgnition ? "Hide Ignition" : "Show Ignition"}>
          <img src={showIgnition ? ignitionlight : ignitiondark} alt="Ignition" style={{ width: 22, height: 22, opacity: showIgnition ? 1 : 0.6 }} />
        </button>
        <button style={btnStyle} onClick={() => setShowSOS(!showSOS)} title={showSOS ? "Hide SOS" : "Show SOS"}>
          <img src={showSOS ? soslight : sosdark} alt="SOS" style={{ width: 22, height: 22, opacity: showSOS ? 1 : 0.6 }} />
        </button>
        <button style={btnStyle} onClick={handleRFIDToggle} title={showRFID ? "Hide RFID" : "Show RFID"}>
          <img src={showRFID ? rfidlight : rfiddark} alt="RFID" style={{ width: 22, height: 22, opacity: showRFID ? 1 : 0.6 }} />
        </button>
      </div>

      <MDBox ref={mapRef} sx={{ width: "100%", height: "100%", overflow: "hidden" }} />
    </>
  );
};

export default RouteReplayMap;